import addWeeks from 'date-fns/add_weeks'
import subWeeks from 'date-fns/sub_weeks'
import { endOfDay, startOfDay } from 'date-fns'
import queryString from 'query-string'
import moment from 'moment'

import { CHANGE_PAGE, SET_EVENTS_FILTER, SET_EVENT_VIEW_TYPE, SET_EVENTS_EXPANDED, SET_CALENDAR_VIEW_TYPE, SET_CALENDAR_LAST_DATE } from './types'
import QueryStringIO from '../../routes/events/lib/query-string-io'
import QueryString from '../../utils/QueryString'
import { DATE_FORMATS } from '../../utils/Formatter'
import Storage from '../../utils/Storage'
import { GET_EVENTS, GET_EVENTS_SLIM } from '../data/types'
import history from '../../history'

export const EVENT_VIEW_TYPES = {
  SLIM: {
    label: 'Slim',
    key: 'SLIM',
  },
  EXPANDED: {
    label: 'Expanded',
    key: 'EXPANDED',
  },
  GRID: {
    label: 'Grid',
    key: 'GRID'
  },
  TABLE: {
    label: 'Table',
    key: 'TABLE'
  },
  CALENDAR: {
    label: 'Calendar',
    key: 'CALENDAR',
  },
}

const defaultViewType = EVENT_VIEW_TYPES.SLIM

const defaultQuerySizes = {
  [EVENT_VIEW_TYPES.SLIM.key]: 50,
  [EVENT_VIEW_TYPES.GRID.key]: 25,
  [EVENT_VIEW_TYPES.EXPANDED.key]: 10,
  [EVENT_VIEW_TYPES.TABLE.key]: 25,
  [EVENT_VIEW_TYPES.CALENDAR.key]: 500
}

const getInitialState = () => {
  // initial viewType comes from: route path, localStorage, or default
  // "/events/slim" => "SLIM"
  const routePath = window.location.pathname.split('/').pop().toUpperCase()
  let viewType = EVENT_VIEW_TYPES[routePath] || EVENT_VIEW_TYPES[Storage.get('EVENT_VIEW_TYPE')] || defaultViewType

  // if it's after 1/1/2022 expanded redirects to slim
  if (new Date().getTime() > new Date('2022/01/01').getTime() && viewType === EVENT_VIEW_TYPES.EXPANDED.key) {
    viewType = EVENT_VIEW_TYPES.SLIM
    history.replace(`/events/${EVENT_VIEW_TYPES.SLIM.key.toLowerCase()}${window.location.search}`)
  }
  
  const storedResultMax = Storage.get('VIEW_RESULT_MAX')
  const resultMax = (storedResultMax && storedResultMax[viewType.key]) || defaultQuerySizes[viewType.key]
  const filterDefaults = {
    anyCategories: [],
    startDateTime: startOfDay(moment(subWeeks(new Date(), 2)).format(DATE_FORMATS.apiDateFormat)),
    stopDateTime: endOfDay(moment(addWeeks(new Date(), 2)).format(DATE_FORMATS.apiDateFormat)),
    getOnlyPrivate: false,
    includePrivate: true,
    showEncoder: true,
    showMediaAssets: true,
    showMediaDetails: true,
    showStreams: true,
    liveOnly: false,
    sortBy: 'DESC',
    totalPages: null,
    currentPage: null,
    search: {},
    techID: undefined,
  }
  const filters = {
    ...filterDefaults,
    ...QueryStringIO._createParams(queryString.parse(location.search), filterDefaults),
    resultMax  
  }
  // getOnlyPrivate must be a boolean
  filters.getOnlyPrivate = filters.getOnlyPrivate === true || filters.getOnlyPrivate === 'true'
  return {
    filters,
    viewType,
    expanded: []
  }
}

const setFilters = (state, action) => {
  return {
    ...state,
    filters: action.payload,
    expanded: []
  }
}

const setEventViewType = (state, action) => {
  const viewType = EVENT_VIEW_TYPES[action.payload.type] || defaultViewType
  const storedResultMax = Storage.get('VIEW_RESULT_MAX')
  let resultMax = (storedResultMax && storedResultMax[viewType.key]) || defaultQuerySizes[viewType.key]
  if (!storedResultMax) {
    Storage.set('VIEW_RESULT_MAX', {
      ...defaultQuerySizes
    })
  }

  // redirect to route for new viewType
  history.replace(`/events/${viewType.key.toLowerCase()}${window.location.search}`)

  if (viewType !== EVENT_VIEW_TYPES.CALENDAR.key) {
    // only the calendar view should request more than 100 events
    // requesting too many events without the slim api can time out requests
    resultMax = Math.min(100, resultMax)
  }
  return {
    ...state,
    viewType,
    filters: {
      ...state.filters,
      resultMax
    },
    expanded: []
  }
}

const changeCurrentPage = (state, action) => {
  return {
    ...state,
    filters: {
      ...state.filters,
      currentPage: action.payload.page
    },
    expanded: []
  }
}

const mergeFilters = (state, action) => {
  //merge in the changed with the existing / default filters
  const newState = {
    ...state,
    filters: {
      ...state.filters,
      ...action.payload,
    },
  }
  return newState
}

const setExpanded = (state, action) => {
  let expanded = action.payload.expanded
  let newExpanded
  if (state.expanded.includes(expanded)) {
    // remove expanded event from expanded list
    newExpanded = state.expanded.filter(event => event !== expanded)
  } else {
    newExpanded = [...state.expanded, expanded]
  }
  return {
    ...state,
    expanded: newExpanded
  }
}

const setFirstEventExpanded = (state, action) => {
  if (state.viewType.key !== EVENT_VIEW_TYPES.SLIM.key) {
    return state
  }
  const events = action.payload.res.data.data
  if (!events) return state
  if (!events.length) return state

  const comparator =
    state.filters.sortBy === 'ASC'
      ? (dateA, dateB) => moment(dateA).isBefore(moment(dateB))
      : (dateA, dateB) => moment(dateA).isAfter(moment(dateB))

  let firstEvent = events[0]
  events.forEach(event => {
    if (comparator(event.startDateTime, firstEvent.startDateTime)) {
      firstEvent = event
    }
  })
  
  return {
    ...state,
    expanded: [
      ...state.expanded,
      firstEvent.eventID
    ]
  }
}

const setCalendarViewType = (state, action) => {
  const { type } = action.payload
  return {
    ...state,
    calendarViewType: type
  }
}

const setCalendarLastDate = (state, action) => {
  const { date } = action.payload
  return {
    ...state,
    calendarLastDate: date
  }
}

const actionHandlers = {
  [SET_EVENTS_FILTER]: setFilters,
  [SET_EVENT_VIEW_TYPE]: setEventViewType,
  [CHANGE_PAGE]: changeCurrentPage,
  'BUS/filterChange': mergeFilters,
  'BUS/search': mergeFilters,
  [SET_EVENTS_EXPANDED]: setExpanded,
  [SET_CALENDAR_VIEW_TYPE]: setCalendarViewType,
  [SET_CALENDAR_LAST_DATE]: setCalendarLastDate
}

const requestHandlers = {
  [`${GET_EVENTS}_FETCH_SUCCESS`]: setFirstEventExpanded,
  [`${GET_EVENTS_SLIM}_FETCH_SUCCESS`]: setFirstEventExpanded,
  'BUS/http.events.get.resolve': setFirstEventExpanded,
}

const reducer = (state = getInitialState(), action) => {
  const handlers = [actionHandlers, requestHandlers]
  for (let i = 0; i < handlers.length; i++) {
    const handler = handlers[i]
    if (Object.keys(handler).includes(action.type)) {
      return handler[action.type](state, action)
    }
  }
  return state
}

export default reducer
